import { Lightbulb } from 'lucide-react'
import { BuildToolButton } from './BuildToolButton'
import type { SuggestedAction, BuildToolResponse } from '@/api/plugins'

interface SuggestedActionsProps {
  actions: SuggestedAction[]
  onActionComplete?: (action: SuggestedAction, response: BuildToolResponse) => void
}

/**
 * Container component for displaying suggested actions.
 *
 * Renders a list of actionable suggestions (like "Build this tool")
 * that the user can click to trigger backend operations.
 */
export function SuggestedActions({
  actions,
  onActionComplete,
}: SuggestedActionsProps) {
  if (!actions || actions.length === 0) {
    return null
  }

  return (
    <div className="mt-3 pt-3 border-t border-white/10">
      <div className="flex items-center gap-2 text-xs text-text-muted mb-2">
        <Lightbulb className="w-3 h-3" />
        <span>Suggested Actions</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {actions.map((action, index) => {
          // Handle different action types
          if (action.action_type === 'build_tool') {
            return (
              <BuildToolButton
                key={`${action.tool_name}-${index}`}
                action={action}
                onComplete={(response) => onActionComplete?.(action, response)}
              />
            )
          }

          // Generic action button for other types
          return (
            <button
              key={`${action.action_type}-${index}`}
              className="
                inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                bg-surface-hover text-text-secondary
                hover:bg-surface-border hover:text-text
                transition-colors duration-200
              "
              title={action.description}
              onClick={() => {
                // For non-build actions, just log for now
                console.log('Action clicked:', action)
              }}
            >
              {action.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Re-export types for convenience
export type { SuggestedAction } from '@/api/plugins'
